import React, { useMemo, useState } from 'react';

import {
  BarChart2,
  Users,
  Search,
  FileText,
  Send,
  ChevronDown,
  Info,
  List,
  BanknoteArrowDown,
  BanknoteArrowUp,
  CheckCircle,
  ReceiptText,
} from 'lucide-react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { useNavigate } from 'react-router-dom';

import { getAdminId, getPermissions, getRoles } from '../../utils/tokenUtils';
import SimpleAlert from '../common/SimpleAlert';

import 'react-perfect-scrollbar/dist/css/styles.css';

interface MenuItem {
  label: string;
  icon?: React.ReactNode;
  route?: string;
  requiredPermission?: string;
  children?: MenuItem[]; // Sub-items
}

interface MenuGroup {
  group: string;
  items: MenuItem[];
}

interface SidebarProps {
  isOpen: boolean;
  activePage: string;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, activePage }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const roles = getRoles() || [];
  const permissions = getPermissions() || [];
  const navigate = useNavigate();
  const adminId = getAdminId();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  const handleNavigation = (page: string): void => {
    navigate(page);
  };

  const toggleExpand = (label: string) => {
    setExpandedItems((prev) =>
      prev.includes(label)
        ? prev.filter((item) => item !== label)
        : [...prev, label],
    );
  };

  const renderMenuItems = (items: MenuItem[]) => {
    const isRouteActive = (route: string) => {
      // Return false if either path is missing
      if (!activePage || !route) {
        return false;
      }

      const currentPath = activePage.toLowerCase();
      const menuRoute = route.toLowerCase();

      // Check if we're on the exact route
      if (currentPath === menuRoute) {
        return true;
      }

      // Check if we're on a sub-route (detail page, create page, etc.)
      try {
        return currentPath.startsWith(menuRoute + '/');
      } catch {
        // If something goes wrong, fall back to simple comparison
        return false;
      }
    };

    return items.map((item, idx) => {
      const isParentActive = item.children
        ? item.children.some(
            (child) => child.route && isRouteActive(child.route),
          )
        : item.route && isRouteActive(item.route);

      const isExpanded = expandedItems.includes(item.label) || isParentActive;

      return (
        <li key={idx}>
          <a
            onClick={() => {
              if (item.children) {
                toggleExpand(item.label);
              } else if (item.route) {
                handleNavigation(item.route);
              }
            }}
            className={`cg-nav-item ${isParentActive ? 'active' : ''}`}
          >
            {item.icon}
            <span className="flex-1">{item.label}</span>
            {item.children && (
              <ChevronDown
                className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              />
            )}
          </a>
          {item.children && (
            <ul
              className={`relative ml-6 overflow-hidden transition-all duration-200 ease-in-out ${
                isExpanded ? 'max-h-96 mt-1' : 'max-h-0'
              }`}
            >
              <div className="absolute top-0 bottom-0 w-px bg-[rgba(255,255,255,.12)]"></div>
              {item.children.map((child, childIdx) => (
                <li key={`${idx}-${childIdx}`} className="relative">
                  <div className="absolute left-0 top-1/2 w-4 h-px bg-[rgba(255,255,255,.12)]"></div>
                  <a
                    onClick={() => child.route && handleNavigation(child.route)}
                    className={`flex items-center gap-3 py-2 pl-6 transition text-sm cursor-pointer ${
                      child.route && isRouteActive(child.route)
                        ? 'text-accent'
                        : 'text-muted2 hover:text-text'
                    }`}
                  >
                    <span>{child.label}</span>
                  </a>
                </li>
              ))}
            </ul>
          )}
        </li>
      );
    });
  };

  const menuGroups: MenuGroup[] = [
    ...(roles.includes('Admin')
      ? [
          {
            group: 'Main',
            items: [
              {
                label: 'Dashboard',
                icon: <BarChart2 className="w-5 h-5" />,
                route: '/admin/dashboard',
                requiredPermission: 'Main.Dashboard.View',
              },
            ],
          },
          {
            group: 'Manage',
            items: [
              {
                label: 'Invoices',
                icon: <BanknoteArrowUp className="w-5 h-5" />,
                route: '/admin/invoices',
                requiredPermission: 'Manage.Invoice.View',
              },
              {
                label: "Accounts Pack Items",
                icon: <FileText className="w-5 h-5" />,
                route: '/admin/accountspackitems',
                requiredPermission: 'Manage.AccountsPackItem.View',
              },
            ],
          },
          {
            group: 'Settings',
            items: [
{
                label: 'Audit Logs',
                icon: <FileText className="w-5 h-5" />,
                route: '/admin/auditlogs',
                requiredPermission: 'Settings.AuditLog.View',
              },
              {
                label: 'Clients',
                icon: <Users className="w-5 h-5" />,
                route: '/admin/clients',
                requiredPermission: 'Settings.Client.View',
              },
              {
                label: 'Company Info',
                icon: <Info className="w-5 h-5" />,
                route: '/admin/companyinfo',
                requiredPermission: 'Settings.CompanyInfo.View',
              },
              {
                label: 'Invoice Statuses',
                icon: <CheckCircle className="w-5 h-5" />,
                route: '/admin/invoicestatuses',
                requiredPermission: 'Settings.InvoiceStatus.View',
              },
              {
                label: 'Invoice Types',
                icon: <ReceiptText className="w-5 h-5" />,
                route: '/admin/invoicetypes',
                requiredPermission: 'Settings.InvoiceType.View',
              },
              {
                label: 'Users',
                icon: <Users className="w-5 h-5" />,
                route: '/admin/users',
                requiredPermission: 'Settings.Administrators.View',
              },
],
          },
        ]
      : []),
  ];

  const filteredGroups = useMemo(
    () =>
      menuGroups
        .map((group) => ({
          ...group,
          items: group.items.filter(
            (item) =>
              (!item.requiredPermission ||
                permissions.includes(item.requiredPermission)) &&
              item.label.toLowerCase().includes(searchTerm.toLowerCase()),
          ),
        }))
        .filter((group) => group.items.length > 0),
    [menuGroups, permissions, searchTerm],
  );

  return (
    <aside
      id="nav-menu"
      aria-label="Side navigation"
      className={`fixed lg:static z-30 w-[244px] bg-bg border-r border-line h-[calc(100%-4rem)] lg:h-full transition-transform ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0 flex flex-col`}
    >
      {/* Search Box */}
      <div className="relative my-4 px-3">
        <input
          type="search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search here"
          aria-label="Search content"
          className="cg-input !h-9"
        />
        <Search
          className="absolute top-2 right-6 h-4 w-4 cursor-pointer text-muted2"
          aria-hidden="true"
          aria-label="Search icon"
          role="graphics-symbol"
        />
      </div>

      {/* Navigation Links */}
      <PerfectScrollbar
        options={{ suppressScrollX: true }}
        className="flex-1 overflow-hidden"
      >
        <div>
          {filteredGroups.map((group, index) => (
            <div key={index} className="mb-4">
              <h2 className="cg-label px-4 py-2">
                {group.group}
              </h2>
              <ul className="space-y-0.5 px-2">{renderMenuItems(group.items)}</ul>
            </div>
          ))}
        </div>
      </PerfectScrollbar>
      {adminId && (
        <SimpleAlert message="Logged in as member" variant="success" />
      )}

      {/* Footer */}
      <footer className="border-t border-line p-3.5 mt-auto">
        <p className="cg-label">
          &copy; {new Date().getFullYear()} Code Garden. All rights reserved.
        </p>
      </footer>
    </aside>
  );
};

export default Sidebar;
